import React, { useState, useEffect } from "react";
import GenericService from "../../services/GenericService";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

interface ProfilePhoto {
  id: string;
  thumbnail: string | null;
  profile: string | null;
  created_at: string;
  updated_at: string;
}

interface ProfilePhotoFormData {
  thumbnail: string;
  profile: string;
}

const ProfilePhotoManagement: React.FC = () => {
  const navigate = useNavigate();
  const [profilePhotos, setProfilePhotos] = useState<ProfilePhoto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editingProfilePhoto, setEditingProfilePhoto] =
    useState<ProfilePhoto | null>(null);
  const [formData, setFormData] = useState<ProfilePhotoFormData>({
    thumbnail: "",
    profile: "",
  });

  const fetchProfilePhotos = async () => {
    try {
      setLoading(true);
      const response = await GenericService.getProfilePhotos({
        page: 1,
        per_page: 100,
      });

      if (response.profile_photos && Array.isArray(response.profile_photos)) {
        setProfilePhotos(response.profile_photos);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch profile photos",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfilePhotos();
  }, []);

  const handleBack = () => {
    navigate("/admin?tab=1");
  };

  const handleOpenDialog = (profilePhoto?: ProfilePhoto) => {
    if (profilePhoto) {
      setEditingProfilePhoto(profilePhoto);
      setFormData({
        thumbnail: profilePhoto.thumbnail || "",
        profile: profilePhoto.profile || "",
      });
    } else {
      setEditingProfilePhoto(null);
      setFormData({
        thumbnail: "",
        profile: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProfilePhoto(null);
    setFormData({
      thumbnail: "",
      profile: "",
    });
    setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.thumbnail.trim() || !formData.profile.trim()) {
      setError("Both thumbnail and profile image URLs are required");
      return;
    }

    try {
      const submitData = {
        thumbnail: formData.thumbnail.trim(),
        profile: formData.profile.trim(),
      };

      if (editingProfilePhoto) {
        await GenericService.updateProfilePhoto(
          editingProfilePhoto.id,
          submitData,
        );
      } else {
        await GenericService.createProfilePhoto(submitData);
      }

      setSuccess(
        `Profile photo ${editingProfilePhoto ? "updated" : "created"} successfully`,
      );
      handleCloseDialog();
      fetchProfilePhotos();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${editingProfilePhoto ? "update" : "create"} profile photo`,
      );
    }
  };

  const handleDelete = async (profilePhotoId: string) => {
    if (
      !window.confirm("Are you sure you want to delete this profile photo?")
    ) {
      return;
    }

    try {
      await GenericService.deleteProfilePhoto(profilePhotoId);
      setSuccess("Profile photo deleted successfully");
      fetchProfilePhotos();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete profile photo",
      );
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const truncateText = (text: string | null, maxLength: number = 50) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Profile Photo Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage profile photos for user profiles
          </Typography>
        </Box>
      </Box>

      {/* Stats Card */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Profile Photos
              </Typography>
              <Typography variant="h4" component="div">
                {profilePhotos.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                With Thumbnails
              </Typography>
              <Typography variant="h4" component="div">
                {profilePhotos.filter((photo) => photo.thumbnail).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                With Profile Images
              </Typography>
              <Typography variant="h4" component="div">
                {profilePhotos.filter((photo) => photo.profile).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          Profile Photos ({profilePhotos.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Profile Photo
        </Button>
      </Box>

      {/* Profile Photos Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Thumbnail URL</TableCell>
              <TableCell>Profile Image URL</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profilePhotos.map((photo) => (
              <TableRow key={photo.id}>
                <TableCell>
                  {photo.thumbnail ? (
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {truncateText(photo.thumbnail)}
                      </Typography>
                      {photo.thumbnail.startsWith("http") && (
                        <Box
                          component="img"
                          src={photo.thumbnail}
                          alt="Thumbnail"
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: 1,
                            mt: 1,
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {photo.profile ? (
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {truncateText(photo.profile)}
                      </Typography>
                      {photo.profile.startsWith("http") && (
                        <Box
                          component="img"
                          src={photo.profile}
                          alt="Profile"
                          sx={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            borderRadius: 1,
                            mt: 1,
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      N/A
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDate(photo.created_at)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatDate(photo.updated_at)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(photo)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(photo.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {profilePhotos.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No profile photos found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingProfilePhoto ? "Edit Profile Photo" : "Add New Profile Photo"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              margin="dense"
              name="thumbnail"
              label="Thumbnail URL (Optional)"
              type="url"
              fullWidth
              variant="outlined"
              value={formData.thumbnail}
              onChange={handleInputChange}
              placeholder="https://example.com/thumbnail.jpg"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              name="profile"
              label="Profile Image URL (Optional)"
              type="url"
              fullWidth
              variant="outlined"
              value={formData.profile}
              onChange={handleInputChange}
              placeholder="https://example.com/profile.jpg"
              helperText="At least one image URL is required"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingProfilePhoto ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePhotoManagement;
